import { MdCheckCircle, MdErrorOutline, MdRefresh } from 'react-icons/md';
import { VIDEO_SCORING_ENABLED } from './scoring-engine-constants';
import { ScoreMode, ScoreModeType } from './scorecard-constants';
import { PortfolioKpis } from './scorecard-utils';
import { ScorecardGauge } from './ScorecardGauge';

interface CatalogOverviewCardProps {
  kpis: PortfolioKpis;
}

const CatalogOverviewCard = ({ kpis }: CatalogOverviewCardProps) => (
  <div className='scorecard-kpi-card'>
    <span className='scorecard-kpi-card-label'>Catalog Overview</span>
    <div className='scorecard-kpi-card-body'>
      <span className='scorecard-kpi-card-metric'>
        {kpis.totalCount.toLocaleString()} ASIN{kpis.totalCount === 1 ? '' : 's'} Loaded
      </span>
      <span className='scorecard-kpi-card-submetric'>
        {kpis.uniqueBrandsCount} Brand{kpis.uniqueBrandsCount === 1 ? '' : 's'} Detected
      </span>
    </div>
    <div className='scorecard-kpi-card-footer'>
      <MdRefresh />
    </div>
  </div>
);

interface CriticalActionsCardProps {
  kpis: PortfolioKpis;
}

const CriticalActionsCard = ({ kpis }: CriticalActionsCardProps) => (
  <div className='scorecard-kpi-card'>
    <div className='scorecard-kpi-card-header'>
      <span className='scorecard-kpi-card-label'>Critical Actions Queue</span>
      <span className='scorecard-kpi-card-tag scorecard-kpi-card-tag-danger'>Urgent Fixes</span>
    </div>
    <span className='scorecard-kpi-card-metric'>{kpis.redCount}</span>
    <p className='scorecard-kpi-card-caption'>Listings Scoring Under 60%</p>
    <div className='scorecard-kpi-urgency-bar'>
      <div
        className='scorecard-kpi-urgency-segment scorecard-kpi-urgency-danger'
        style={{ width: `${kpis.totalCount ? (kpis.redCount / kpis.totalCount) * 100 : 0}%` }}
      />
      <div
        className='scorecard-kpi-urgency-segment scorecard-kpi-urgency-warning'
        style={{ width: `${kpis.totalCount ? (kpis.amberCount / kpis.totalCount) * 100 : 0}%` }}
      />
      <div
        className='scorecard-kpi-urgency-segment scorecard-kpi-urgency-success'
        style={{ width: `${kpis.totalCount ? (kpis.greenCount / kpis.totalCount) * 100 : 0}%` }}
      />
    </div>
  </div>
);

interface AssetGapsCardProps {
  kpis: PortfolioKpis;
}

const AssetGapsCard = ({ kpis }: AssetGapsCardProps) => (
  <div className='scorecard-kpi-card'>
    <span className='scorecard-kpi-card-label'>High-Impact Asset Gaps</span>
    <div className='scorecard-kpi-asset-gaps'>
      {kpis.missingAplus > 0 ? (
        <div className='scorecard-kpi-asset-badge is-fail'>
          <MdErrorOutline /> {kpis.missingAplus} Listing{kpis.missingAplus === 1 ? '' : 's'} Missing
          A+ Content
        </div>
      ) : (
        <div className='scorecard-kpi-asset-badge is-pass'>
          <MdCheckCircle /> All A+ Content Assets Present
        </div>
      )}
      {/* Video asset gap is hidden while the scraper does not supply video data
          (VIDEO_SCORING_ENABLED). Restoring the flag brings this badge back. */}
      {VIDEO_SCORING_ENABLED &&
        (kpis.missingVideo > 0 ? (
          <div className='scorecard-kpi-asset-badge is-fail'>
            <MdErrorOutline /> {kpis.missingVideo} Listing{kpis.missingVideo === 1 ? '' : 's'}{' '}
            Missing Video
          </div>
        ) : (
          <div className='scorecard-kpi-asset-badge is-pass'>
            <MdCheckCircle /> All Video Assets Present
          </div>
        ))}
    </div>
  </div>
);

interface ScoringDimensionsCardProps {
  scoreMode: ScoreModeType;
  averageScore: number;
  alexaAverage: number | null;
}

const ScoringDimensionsCard = ({
  scoreMode,
  averageScore,
  alexaAverage
}: ScoringDimensionsCardProps) => (
  <div className='scorecard-kpi-card'>
    <div className='scorecard-kpi-card-header'>
      <span className='scorecard-kpi-card-label'>Scoring Dimensions</span>
    </div>
    <ScorecardGauge
      score={scoreMode === ScoreMode.HEALTH ? averageScore : (alexaAverage ?? 0)}
      size={100}
      strokeWidth={8}
    />
    <p className='scorecard-kpi-card-caption'>
      {scoreMode === ScoreMode.HEALTH
        ? 'Structural compliance — instant, no AI cost.'
        : 'AI-shopper query coverage — assessed on demand.'}
    </p>
  </div>
);

interface ScorecardKpiCardsProps {
  kpis: PortfolioKpis;
  scoreMode: ScoreModeType;
  alexaAverage: number | null;
}

export const ScorecardKpiCards = ({ kpis, scoreMode, alexaAverage }: ScorecardKpiCardsProps) => (
  <div className='scorecard-kpi-row'>
    <CatalogOverviewCard kpis={kpis} />
    <CriticalActionsCard kpis={kpis} />
    <AssetGapsCard kpis={kpis} />
    <ScoringDimensionsCard
      scoreMode={scoreMode}
      averageScore={kpis.averageScore}
      alexaAverage={alexaAverage}
    />
  </div>
);
